import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Backdrop, Box, Button, IconButton, Link, Modal, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { motion } from "motion/react";
import ConfettiCanvas from "./ConfettiCanvas";
import Balloons from "./Balloons";
import { getMilestonePalette } from "./milestonePalette";
import { generateButtonSx } from "../../utils/elementor-style-override";

// How long one milestone stays up before closing itself. Long enough to read
// the card and watch the shower rather than being hurried off it.
const AUTO_CLOSE_MS = 30000;

// Length of one balloon rise cycle. Independent of the modal's lifetime.
const ANIMATION_CYCLE_MS = 9000;

// Matches the sidebar's Leadership Bonuses entry and the slug the milestone
// email already links to.
const LEADERSHIP_BONUSES_URL = "/leadership-bonuses";

// Beat between queued milestones, so the next card reads as a new award rather
// than a flicker of the last one.
const HANDOVER_MS = 550;

const formatMoney = (value) => `$${Number(value || 0).toLocaleString("en-US")}`;
const formatCount = (value) => Number(value || 0).toLocaleString("en-US");

/**
 * Celebrates one milestone at a time. An import can push an agent across more
 * than one tier at once — rare, but when it happens each tier gets its own card
 * in ascending order, so the run builds toward the biggest.
 */
const MilestoneCelebration = ({
	open,
	onClose,
	onMilestoneShown,
	agentName = "",
	avatarUrl = "",
	milestones = [],
	autoCloseMs = AUTO_CLOSE_MS,
	animationCycleMs = ANIMATION_CYCLE_MS,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

	const queue = useMemo(() => [...(milestones || [])].sort((a, b) => Number(a.target) - Number(b.target)), [milestones]);

	const [index, setIndex] = useState(0);
	const [visible, setVisible] = useState(true);

	const milestone = queue[index];
	const palette = useMemo(() => getMilestonePalette(milestone?.target), [milestone?.target]);

	// Callbacks live in refs so an unstable parent cannot restart the auto-close
	// countdown on every render.
	const closeRef = useRef(onClose);
	const shownRef = useRef(onMilestoneShown);
	const handoverRef = useRef(null);

	useEffect(() => {
		closeRef.current = onClose;
		shownRef.current = onMilestoneShown;
	}, [onClose, onMilestoneShown]);

	useEffect(() => () => clearTimeout(handoverRef.current), []);

	// Restart the run whenever the parent reopens it.
	useEffect(() => {
		if (open) {
			setIndex(0);
			setVisible(true);
		}
	}, [open]);

	useEffect(() => {
		if (open && visible && milestone) shownRef.current?.(milestone);
	}, [open, visible, milestone]);

	const handleDismiss = useCallback(() => {
		// Hiding first unmounts the confetti and balloons, whose cleanup cancels
		// their timers. The "Thank you" button, the X, Escape and the backdrop all
		// land here.
		setVisible(false);

		clearTimeout(handoverRef.current);
		handoverRef.current = setTimeout(() => {
			setIndex((i) => {
				const next = i + 1;
				if (next < queue.length) {
					setVisible(true);
					return next;
				}
				closeRef.current?.();
				return i;
			});
		}, HANDOVER_MS);
	}, [queue.length]);

	useEffect(() => {
		if (!open || !visible || !autoCloseMs) return undefined;

		const timer = setTimeout(handleDismiss, autoCloseMs);
		return () => clearTimeout(timer);
	}, [open, visible, index, autoCloseMs, handleDismiss]);

	const firstName = useMemo(() => (agentName || "").trim().split(/\s+/)[0] || "there", [agentName]);
	const initials = useMemo(
		() =>
			(agentName || "")
				.trim()
				.split(/\s+/)
				.slice(0, 2)
				.map((p) => p[0] || "")
				.join("")
				.toUpperCase(),
		[agentName]
	);

	if (!open || !milestone) return null;

	const animate = !prefersReducedMotion;
	const avatarSize = isMobile ? 104 : 136;
	const showEffects = animate && visible;

	return (
		<>
			{showEffects && (
				<>
					{/* Effects deliberately stay on the brand palette: the tier colour
					    is the card's, so the surrounding scene reads the same every time. */}
					<Balloons duration={animationCycleMs} active />
					{/* Tracks the close so the shower tapers off instead of being cut mid-fall. */}
					<ConfettiCanvas duration={autoCloseMs || 60000} active />
				</>
			)}

			<Modal
				open={visible}
				onClose={handleDismiss}
				aria-labelledby="ahg-milestone-title"
				aria-describedby="ahg-milestone-message"
				closeAfterTransition
				slots={{ backdrop: Backdrop }}
				slotProps={{
					backdrop: {
						sx: { backgroundColor: "rgba(16, 37, 80, 0.55)", backdropFilter: "blur(4px)" },
					},
				}}
				sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
			>
				<Box
					component={motion.div}
					// Keyed on the target so a queued handover remounts the card and
					// replays the entrance instead of morphing the previous one.
					key={milestone.target}
					initial={animate ? { opacity: 0, scale: 0.85, y: 24 } : false}
					animate={{ opacity: 1, scale: 1, y: 0 }}
					transition={animate ? { type: "spring", stiffness: 210, damping: 20 } : { duration: 0 }}
					sx={{
						position: "relative",
						width: "100%",
						maxWidth: 460,
						maxHeight: "92vh",
						overflowY: "auto",
						outline: "none",
						borderRadius: 4,
						px: { xs: 3, sm: 5 },
						pt: { xs: 5, sm: 6 },
						pb: { xs: 3.5, sm: 4.5 },
						textAlign: "center",
						background: `linear-gradient(165deg, #FFFFFF 0%, ${alpha(palette.bg, 0.65)} 55%, ${palette.bg} 100%)`,
						boxShadow: `0 24px 68px ${alpha(palette.deep, 0.35)}`,
						border: `1px solid ${alpha(palette.main, 0.4)}`,
					}}
				>
					<IconButton
						onClick={handleDismiss}
						aria-label="Dismiss celebration"
						size="small"
						sx={{
							position: "absolute",
							top: 10,
							right: 10,
							color: alpha(palette.deep, 0.55),
							backgroundColor: "transparent !important",
							...generateButtonSx(
								{ bg: `${alpha(palette.main, 0.16)} !important`, text: palette.deep },
								{ bg: `${alpha(palette.main, 0.16)} !important`, text: palette.deep }
							),
						}}
					>
						<CloseIcon fontSize="small" />
					</IconButton>

					{queue.length > 1 && (
						<Typography
							variant="caption"
							sx={{ position: "absolute", top: 16, left: 20, fontWeight: 700, color: alpha(palette.deep, 0.55) }}
						>
							{index + 1} of {queue.length}
						</Typography>
					)}

					<Box sx={{ position: "relative", display: "inline-flex", mb: 2.5 }}>
						{animate && (
							<Box
								component={motion.div}
								aria-hidden="true"
								animate={{ scale: [1, 1.18, 1], opacity: [0.55, 0.2, 0.55] }}
								transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
								sx={{
									position: "absolute",
									inset: -14,
									borderRadius: "50%",
									background: `radial-gradient(circle, ${alpha(palette.main, 0.6)} 0%, ${alpha(palette.main, 0)} 70%)`,
								}}
							/>
						)}
						<Avatar
							src={avatarUrl || undefined}
							alt={agentName}
							sx={{
								width: avatarSize,
								height: avatarSize,
								fontSize: avatarSize / 3,
								fontWeight: 700,
								bgcolor: palette.main,
								color: "#fff",
								border: "4px solid #FFFFFF",
								boxShadow: `0 10px 28px ${alpha(palette.deep, 0.4)}`,
							}}
						>
							{initials}
						</Avatar>
						<Box
							aria-hidden="true"
							sx={{
								position: "absolute",
								bottom: -4,
								right: -4,
								width: 46,
								height: 46,
								borderRadius: "50%",
								display: "grid",
								placeItems: "center",
								background: "linear-gradient(140deg, #FFC14D 0%, #F4A11A 100%)",
								border: "3px solid #FFFFFF",
								boxShadow: "0 6px 16px rgba(244, 161, 26, 0.45)",
							}}
						>
							<EmojiEventsIcon sx={{ fontSize: 24, color: "#FFFFFF" }} />
						</Box>
					</Box>

					<Typography id="ahg-milestone-title" variant="h5" sx={{ fontWeight: 800, color: palette.deep, letterSpacing: "-0.4px" }}>
						Congratulations, {firstName}!
					</Typography>

					<Typography
						variant="overline"
						sx={{ display: "block", mt: 2, mb: milestone.name ? 0 : 1.5, color: palette.main, fontWeight: 700, letterSpacing: 1.4 }}
					>
						Milestone Unlocked
					</Typography>

					{milestone.name && (
						<Typography variant="h6" sx={{ fontWeight: 700, color: palette.deep, mb: 1.5 }}>
							{milestone.name}
						</Typography>
					)}

					<Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
						<Box
							sx={{
								px: 1.75,
								py: 0.5,
								borderRadius: 999,
								bgcolor: "success.verylight",
								color: "success.dark",
								fontWeight: 700,
								fontSize: 14,
							}}
						>
							{formatMoney(milestone.reward)} bonus
						</Box>
						<Box
							sx={{
								px: 1.75,
								py: 0.5,
								borderRadius: 999,
								bgcolor: alpha(palette.main, 0.14),
								color: palette.deep,
								fontWeight: 700,
								fontSize: 14,
							}}
						>
							{formatCount(milestone.target)} members
						</Box>
					</Stack>

					<Typography id="ahg-milestone-message" variant="body2" sx={{ color: "text.secondary", mb: 1.25, px: { sm: 1 } }}>
						You&rsquo;ve grown your network to {formatCount(milestone.target)} members and earned your leadership bonus. Thank you for the
						work you put in &mdash; keep building.
					</Typography>

					{/* Navigating away is a fine exit: the milestone was latched the moment
					    the card appeared, so it will not replay on the next page. */}
					<Typography variant="body2" sx={{ color: "text.secondary", mb: 3, px: { sm: 1 } }}>
						For more details, go to the{" "}
						<Link
							href={LEADERSHIP_BONUSES_URL}
							sx={{
								// Elementor styles bare anchors on the page, so the colour
								// and underline both have to be stated outright.
								color: `${palette.deep} !important`,
								fontWeight: 700,
								textDecorationColor: alpha(palette.deep, 0.4),
								"&:hover": { textDecorationColor: palette.deep },
							}}
						>
							Leadership Bonuses
						</Link>{" "}
						page.
					</Typography>

					<Button
						onClick={handleDismiss}
						variant="contained"
						size="large"
						autoFocus
						fullWidth={isMobile}
						sx={{
							px: 5,
							py: 1.25,
							borderRadius: 999,
							fontWeight: 700,
							textTransform: "none",
							fontSize: 16,
							boxShadow: `0 8px 20px ${alpha(palette.main, 0.45)}`,
							background: `linear-gradient(135deg, ${palette.main} 0%, ${palette.deep} 100%)`,
							"&:hover": { background: `linear-gradient(135deg, ${palette.light} 0%, ${palette.main} 100%)` },
						}}
					>
						Thank you!
					</Button>
				</Box>
			</Modal>
		</>
	);
};

export default MilestoneCelebration;
